<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { ArrowRight, BookOpen, CalendarDays, Database, LockKeyhole, LogOut, Phone, RefreshCw } from 'lucide-vue-next'
import { getCourses, getMyCourses, getProfile, getVipInfo, isSmsLoginConfigured, loginWithSms, logout, rows, sendSmsCode } from '../services/api'
import { firstMediaUrl } from '../utils/media'

const phone = ref('')
const code = ref('')
const loading = ref(false)
const sending = ref(false)
const error = ref('')
const profile = ref(null)
const courseCount = ref(0)
const purchasedCourses = ref([])
const coursesLoading = ref(false)
const coursesError = ref('')
const vip = ref(null)
const countdown = ref(0)
let timer

function userValue(key) { return profile.value?.user?.[key] || profile.value?.[key] || '' }
function isVipActive() {
  return Boolean(vip.value && Number(vip.value.vipFlag) === 0 && Number(vip.value.expireFlag) === 0)
}
function vipValidTime() {
  if (!isVipActive()) return '未开通'
  const value = vip.value?.validTime || vip.value?.endTime || vip.value?.expireTime || vip.value?.vipEndTime
  return value ? String(value).slice(0, 10) : '长期有效'
}
function courseId(item = {}) {
  return item.curriculumId ?? item.courseId ?? item.curriculum?.id ?? item.course?.id
}
function courseTitle(item = {}) {
  return item.title || item.curriculumTitle || item.courseTitle || item.curriculum?.title || item.course?.title || '已开通课程'
}
function courseCover(item = {}) {
  return firstMediaUrl(item.images || item.curriculumImages || item.courseImages || item.curriculum?.images || item.course?.images) || '/assets/logo.png'
}
function courseSummary(item = {}) {
  return item.lights || item.curriculumLights || item.description || item.curriculum?.lights || '课程学习权益已生效，可直接进入学习。'
}
async function loadCourses() {
  coursesLoading.value = true
  coursesError.value = ''
  try {
    const ordersPayload = await getMyCourses({ pageNum: 1, pageSize: 500 })
    const orderRows = rows(ordersPayload)
    const catalog = await getCourses({ pageNum: 1, pageSize: 500 }).catch(() => [])
    const catalogById = new Map(catalog.map(item => [String(item.id), item]))
    const uniqueCourses = new Map()

    orderRows.forEach(order => {
      const id = courseId(order)
      if (id === undefined || id === null || String(id) === '') return
      const detail = catalogById.get(String(id)) || {}
      const merged = { ...order, ...detail, id }
      uniqueCourses.set(String(id), {
        ...merged,
        title: courseTitle(merged),
        images: courseCover(merged),
        summary: courseSummary(merged)
      })
    })

    purchasedCourses.value = [...uniqueCourses.values()]
    courseCount.value = purchasedCourses.value.length
  } catch (e) {
    purchasedCourses.value = []
    courseCount.value = 0
    coursesError.value = e?.message || '课程列表暂时无法读取，请稍后重试。'
  } finally {
    coursesLoading.value = false
  }
}
async function load() {
  try {
    profile.value = await getProfile()
    vip.value = await getVipInfo().catch(() => null)
    await loadCourses()
  } catch { profile.value = null }
}
function validatePhone() {
  if (!/^1\d{10}$/.test(phone.value)) throw new Error('请输入正确的 11 位手机号')
}
async function sendCode() {
  error.value = ''
  try {
    validatePhone()
    if (!isSmsLoginConfigured()) throw new Error('安全验证码接口正在接入若依后台，当前暂不开放网页登录')
    sending.value = true
    await sendSmsCode(phone.value)
    countdown.value = 60
    timer = window.setInterval(() => {
      countdown.value -= 1
      if (countdown.value <= 0) window.clearInterval(timer)
    }, 1000)
  } catch (e) { error.value = e.message } finally { sending.value = false }
}
async function login() {
  loading.value = true
  error.value = ''
  try {
    validatePhone()
    if (!/^\d{4,8}$/.test(code.value)) throw new Error('请输入短信验证码')
    await loginWithSms(phone.value, code.value)
    await load()
  } catch (e) { error.value = e.message } finally { loading.value = false }
}
function exit() {
  logout(); profile.value = null; vip.value = null; courseCount.value = 0; purchasedCourses.value = []; coursesError.value = ''; phone.value = ''; code.value = ''
}
onMounted(load)
onBeforeUnmount(() => window.clearInterval(timer))
</script>

<template>
  <main class="account-page">
    <section class="section"><div class="container account-layout">
      <div class="account-copy"><p class="eyebrow">TRADE365 ACCOUNT</p><h1>{{ profile ? '欢迎回来' : '一个账号，同步全部学习权益' }}</h1><p>使用后台绑定的手机号和短信验证码登录，课程购买记录、VIP 有效期及数据权限会与小程序保持一致。</p><div class="benefits"><span><BookOpen />已购课程</span><span><CalendarDays />会员有效期</span><span><Database />数据权益</span></div></div>
      <div v-if="!profile" class="login-panel"><img src="/assets/logo.png" alt="外贸365" /><h2>手机号登录</h2><p>验证一次，本设备 7 天内免登录。</p><form @submit.prevent="login"><label><Phone :size="18" /><input v-model.trim="phone" inputmode="tel" maxlength="11" placeholder="请输入手机号" required /></label><label class="code-field"><LockKeyhole :size="18" /><input v-model.trim="code" inputmode="numeric" maxlength="8" placeholder="短信验证码" required /><button type="button" :disabled="sending || countdown > 0" @click="sendCode">{{ countdown > 0 ? `${countdown} 秒` : '获取验证码' }}</button></label><button class="button primary wide" :disabled="loading">{{ loading ? '登录中' : '登录外贸365' }}</button><p v-if="error" class="login-error">{{ error }}</p></form><small>为保护会员权益，网页端只使用经过短信验证的安全登录。</small></div>
      <div v-else class="profile-panel"><p class="eyebrow">MY ACCOUNT</p><h2>{{ userValue('nickName') || '外贸365学员' }}</h2><p>{{ userValue('phonenumber') }}</p><div class="account-stats"><div><span>已购课程</span><strong>{{ courseCount }}</strong></div><div><span>会员有效期</span><strong class="vip-date">{{ vipValidTime() }}</strong></div></div><div class="profile-stat"><span>会员状态</span><strong>{{ isVipActive() ? '有效会员' : '普通学员' }}</strong></div><RouterLink class="button primary wide" to="/courses">继续学习</RouterLink><button class="button ghost wide" @click="exit"><LogOut :size="17" />退出登录</button></div>
    </div></section>

    <section v-if="profile" class="account-courses-section">
      <div class="container">
        <header class="account-courses-heading">
          <div>
            <h2>我的课程</h2>
            <p>支付购买和后台开通的课程都会显示在这里。</p>
          </div>
          <span v-if="!coursesLoading && !coursesError">共 {{ courseCount }} 门</span>
        </header>

        <div v-if="coursesLoading" class="account-course-grid" aria-label="正在加载我的课程" aria-busy="true">
          <div v-for="item in 4" :key="item" class="account-course-card course-skeleton">
            <span></span><div><i></i><i></i><i></i></div>
          </div>
        </div>

        <div v-else-if="coursesError" class="course-state course-error" role="status">
          <BookOpen :size="28" />
          <h3>课程暂时没有加载出来</h3>
          <p>{{ coursesError }}</p>
          <button class="button ghost" type="button" @click="loadCourses"><RefreshCw :size="17" />重新加载</button>
        </div>

        <div v-else-if="purchasedCourses.length" class="account-course-grid">
          <article v-for="course in purchasedCourses" :key="course.id" class="account-course-card">
            <RouterLink class="account-course-cover" :to="`/courses/${course.id}/learn`">
              <img :src="course.images" :alt="course.title" loading="lazy" />
            </RouterLink>
            <div class="account-course-body">
              <span class="course-access"><BookOpen :size="15" />学习权益已生效</span>
              <h3><RouterLink :to="`/courses/${course.id}/learn`">{{ course.title }}</RouterLink></h3>
              <p>{{ course.summary }}</p>
              <RouterLink class="course-learn-link" :to="`/courses/${course.id}/learn`">继续学习<ArrowRight :size="17" /></RouterLink>
            </div>
          </article>
        </div>

        <div v-else class="course-state">
          <BookOpen :size="28" />
          <h3>还没有已购课程</h3>
          <p>课程购买或由后台开通后，会自动出现在这里。</p>
          <RouterLink class="button primary" to="/courses">查看课程</RouterLink>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.account-page{min-height:75vh;background:var(--soft)}.account-layout{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(360px,.7fr);gap:70px;align-items:center;min-height:620px}.account-copy h1{max-width:760px;font-size:clamp(40px,6vw,72px);line-height:1.05;margin:12px 0 24px;letter-spacing:-.055em}.account-copy>p{max-width:720px;color:var(--muted);font-size:18px;line-height:1.85}.benefits{display:flex;gap:22px;flex-wrap:wrap;margin-top:34px}.benefits span{display:flex;align-items:center;gap:8px;font-weight:800}.benefits svg{color:var(--brand)}.login-panel,.profile-panel{padding:34px;border:1px solid var(--line);background:var(--surface)}.login-panel img{width:150px}.login-panel h2,.profile-panel h2{font-size:30px;margin:25px 0 8px}.login-panel>p,.login-panel small,.profile-panel>p{color:var(--muted)}.login-panel form{display:grid;gap:13px;margin:25px 0}.login-panel label{display:flex;align-items:center;gap:10px;padding:14px;border:1px solid var(--line);background:var(--surface)}.login-panel input{min-width:0;width:100%;border:0;outline:0;background:transparent;color:var(--text);font:inherit}.code-field button{flex:0 0 auto;padding:0;border:0;background:none;color:var(--blue);font-weight:800}.code-field button:disabled{color:var(--muted)}.wide{width:100%;justify-content:center}.login-error{margin:0;color:#bd3c3c;line-height:1.5}.profile-stat{display:flex;justify-content:space-between;padding:20px 0;margin:24px 0;border-block:1px solid var(--line)}.account-stats{margin-top:26px;display:grid;grid-template-columns:1fr 1fr;border:1px solid var(--line)}.account-stats div{padding:20px}.account-stats div+div{border-left:1px solid var(--line)}.account-stats span{display:block;color:var(--muted);font-size:13px}.account-stats strong{display:block;margin-top:5px;font-size:28px}.account-stats .vip-date{font-size:18px;line-height:1.55;white-space:nowrap}.profile-panel .ghost{margin-top:10px}
.account-courses-section{padding:72px 0 94px;border-top:1px solid var(--line);background:var(--canvas)}.account-courses-heading{margin-bottom:30px;display:flex;align-items:flex-end;justify-content:space-between;gap:30px}.account-courses-heading h2{margin:0;font-size:clamp(32px,4vw,48px);letter-spacing:.015em}.account-courses-heading p{margin:10px 0 0;color:var(--muted);line-height:1.65}.account-courses-heading>span{flex:0 0 auto;color:var(--muted);font-size:14px}.account-course-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px}.account-course-card{min-width:0;display:grid;grid-template-columns:180px minmax(0,1fr);overflow:hidden;border:1px solid var(--line);border-radius:var(--radius);background:var(--surface);transition:border-color .2s ease,transform .2s ease}.account-course-card:hover{border-color:var(--line-strong);transform:translateY(-2px)}.account-course-card:active{transform:translateY(0)}.account-course-cover{display:block;min-height:230px;background:var(--navy)}.account-course-cover img{width:100%;height:100%;display:block;object-fit:cover}.account-course-body{min-width:0;padding:22px;display:flex;flex-direction:column}.course-access{display:flex;align-items:center;gap:7px;color:var(--mint);font-size:12px;font-weight:800}.account-course-body h3{margin:14px 0 9px;font-size:19px;line-height:1.4}.account-course-body p{display:-webkit-box;margin:0;color:var(--muted);font-size:13px;line-height:1.65;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2}.course-learn-link{margin-top:auto;padding-top:18px;display:inline-flex;align-items:center;gap:7px;color:var(--blue);font-weight:800}.course-learn-link svg{transition:transform .2s ease}.course-learn-link:hover svg{transform:translateX(3px)}.course-state{padding:58px 24px;border:1px solid var(--line);border-radius:var(--radius);background:var(--surface);text-align:center;color:var(--muted)}.course-state svg{color:var(--blue)}.course-state h3{margin:16px 0 8px;color:var(--text);font-size:22px}.course-state p{margin:0 0 22px;line-height:1.65}.course-error svg{color:var(--danger)}.course-skeleton{pointer-events:none}.course-skeleton>span{min-height:230px;background:var(--canvas-deep);animation:skeleton-pulse 1.2s ease-in-out infinite alternate}.course-skeleton>div{padding:22px}.course-skeleton i{height:14px;margin-bottom:14px;display:block;background:var(--canvas-deep);animation:skeleton-pulse 1.2s ease-in-out infinite alternate}.course-skeleton i:nth-child(1){width:42%}.course-skeleton i:nth-child(2){width:88%;height:22px}.course-skeleton i:nth-child(3){width:68%}
@media(max-width:1000px){.account-course-card{grid-template-columns:145px minmax(0,1fr)}.account-course-cover,.course-skeleton>span{min-height:205px}}
@media(max-width:800px){.account-layout{grid-template-columns:1fr;gap:36px;min-height:auto}.account-copy h1{font-size:42px}.account-course-grid{grid-template-columns:1fr}}
@media(max-width:560px){.account-layout{gap:30px}.account-copy h1{font-size:38px}.login-panel,.profile-panel{padding:26px 22px}.account-courses-section{padding:52px 0 70px}.account-courses-heading{align-items:flex-start}.account-courses-heading p{font-size:14px}.account-course-card{grid-template-columns:128px minmax(0,1fr)}.account-course-cover,.course-skeleton>span{min-height:178px}.account-course-body{padding:16px}.account-course-body h3{margin-top:10px;font-size:16px}.account-course-body p{display:none}.course-learn-link{padding-top:13px;font-size:13px}}
</style>
